import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';

interface ContextType {
  registerRef: (ref: HTMLAudioElement) => void;
  registerHowlRef: (ref: Howl) => void;
  unregisterRef: (ref: Howl | HTMLAudioElement) => void;
  stopAll: (isRecording?: boolean) => void;
  stopRecording: () => void;
}

export const AudioContext = createContext<Partial<ContextType>>({});

export const AudioContextProvider = ({ children }: { children: React.ReactNode }) => {
  const audioHowlRefsRef = useRef<Howl[]>([]);
  const audioRefsRef = useRef<HTMLAudioElement[]>([]);
  const isRecordingRef = useRef(false);

  const registerRef = useCallback((ref: HTMLAudioElement) => {
    if (!ref) {
      return;
    }
    if (isRecordingRef.current) {
      ref.currentTime = 0;
      ref.pause();
    }

    if (!isRecordingRef.current && !audioRefsRef.current.includes(ref)) {
      audioRefsRef.current = [...audioRefsRef.current, ref];
    }
  }, []);

  const registerHowlRef = useCallback((ref: Howl) => {
    if (!ref) {
      return;
    }
    if (isRecordingRef.current) {
      ref.stop();
    }

    if (!isRecordingRef.current && !audioHowlRefsRef.current.includes(ref)) {
      audioHowlRefsRef.current = [...audioHowlRefsRef.current, ref];
    }
  }, []);

  const unregisterRef = useCallback((targetRef: Howl | HTMLAudioElement) => {
    if (!targetRef) {
      return;
    }
    audioHowlRefsRef.current = audioHowlRefsRef.current.filter((howlRef) => howlRef !== targetRef);
    audioRefsRef.current = audioRefsRef.current.filter((audioRef) => audioRef !== targetRef);
  }, []);

  const stopAll = useCallback((isRecording = false) => {
    if (!isRecordingRef.current) {
      isRecordingRef.current = isRecording;
    }
    audioRefsRef.current.forEach((ref) => {
      ref.currentTime = 0;
      ref.pause();
    });
    audioHowlRefsRef.current.forEach((ref) => {
      if (ref && typeof ref.stop === 'function') {
        ref.stop();
      }
    });
  }, []);

  const stopRecording = useCallback(() => {
    isRecordingRef.current = false;
  }, []);

  const contextValue = useMemo(
    () => ({
      registerRef,
      registerHowlRef,
      unregisterRef,
      stopAll,
      stopRecording,
    }),
    [registerRef, registerHowlRef, unregisterRef, stopAll, stopRecording]
  );

  return <AudioContext.Provider value={contextValue}>{children}</AudioContext.Provider>;
};

export const useAudioContext = () => {
  const context = useContext(AudioContext);
  if (context === undefined) {
    throw new Error('useAudioContext must be used within a AudioProvider');
  }
  return context;
};
